/**
 * React 18 Polyfill for React 16 - ES Module Version
 *
 * This file shims React 18 APIs that @chargebee/sting-react depends on,
 * allowing sting-react to work in a React 16 environment.
 *
 * When @chargebee/sting-react does `import { Children } from 'react'`, webpack
 * redirects the import to this ES module polyfill instead.
 */

import * as React from "react";

// ============================================================================
// React 18 API Shims
// ============================================================================

/**
 * useId - React 18 hook that generates unique IDs.
 * Shim: Uses a module-level counter + useRef to produce stable unique IDs.
 */
let idCounter = 0;
function useId() {
    const ref = React.useRef(null);
    if (ref.current === null) {
        ref.current = ":r" + (idCounter++).toString(32) + ":";
    }
    return ref.current;
}

/**
 * useSyncExternalStore - React 18 hook for subscribing to external stores.
 * Shim: Uses useState + useEffect to subscribe and sync.
 */
function useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) {
    const value = getSnapshot();
    const [{ inst }, forceUpdate] = React.useState({
        inst: { value, getSnapshot }
    });

    React.useLayoutEffect(() => {
        inst.value = value;
        inst.getSnapshot = getSnapshot;

        if (checkIfSnapshotChanged(inst)) {
            forceUpdate({ inst });
        }
    }, [subscribe, value, getSnapshot]);

    React.useEffect(() => {
        if (checkIfSnapshotChanged(inst)) {
            forceUpdate({ inst });
        }
        const handleStoreChange = () => {
            if (checkIfSnapshotChanged(inst)) {
                forceUpdate({ inst });
            }
        };
        return subscribe(handleStoreChange);
    }, [subscribe]);

    return value;
}

function checkIfSnapshotChanged(inst) {
    const latestGetSnapshot = inst.getSnapshot;
    const prevValue = inst.value;
    try {
        const nextValue = latestGetSnapshot();
        return !Object.is(prevValue, nextValue);
    } catch (error) {
        return true;
    }
}

/**
 * useInsertionEffect - React 18 hook for CSS-in-JS libraries.
 * Shim: Falls back to useLayoutEffect (close enough for most use cases).
 */
const useInsertionEffect = React.useLayoutEffect;

/**
 * startTransition - React 18 concurrent feature for non-urgent updates.
 * Shim: Simply executes the callback immediately (no concurrent behavior).
 */
function startTransition(callback) {
    callback();
}

/**
 * useTransition - React 18 hook for transitions.
 * Shim: Returns [false, startTransition] (never "pending").
 */
function useTransition() {
    return [false, startTransition];
}

/**
 * useDeferredValue - React 18 hook to defer re-rendering of a value.
 * Shim: Returns the value as-is (no deferral in React 16).
 */
function useDeferredValue(value) {
    return value;
}

// ============================================================================
// Re-export everything from real React + our shims
// ============================================================================

// Export everything from React
export * from "react";

// Export our polyfill functions
export {
    useId,
    useSyncExternalStore,
    useInsertionEffect,
    startTransition,
    useTransition,
    useDeferredValue
};

// Create default export that includes everything
const ReactWithPolyfills = {
    ...React,
    useId,
    useSyncExternalStore,
    useInsertionEffect,
    startTransition,
    useTransition,
    useDeferredValue
};

export default ReactWithPolyfills;
